import { Loader } from '@trackman/web-shared-components';
import { ServiceTypes } from 'constants/ServiceTypes';
import { NotificationType } from 'enums/NotificationType';
import { SessionMode } from 'enums/SessionMode';
import { SessionType } from 'enums/SessionType';
import { History } from 'history';
import { ITeam } from 'interfaces/team/ITeam';
import { observer } from 'mobx-react-lite';
import { LineupManager } from 'models/setLineup/LineupManager';
import SetLineupBottomBar from 'pages/SetLineup/components/SetLineupBottomBar/SetLineupBottomBar';
import { TeamSelector } from 'pages/SetLineup/components/TeamSelector/TeamSelector';
import { TeamSetup } from 'pages/SetLineup/components/TeamSetup/TeamSetup';
import React, { useEffect, useState } from 'react';
import { useInjection } from 'services/ioc/ioc.react';
import RulesetService from 'services/RulesetService';
import { ContextStore } from 'stores/ContextStore';
import { ContextualDataStore } from 'stores/ContextualDataStore';
import { LineupStore } from 'stores/LineupStore';
import { NotificationStore } from 'stores/NotificationStore';
import { SessionStore } from 'stores/SessionStore';
import { BpTaggingStore } from 'stores/Tagging/BpTaggingStore';
import { TeamStore } from 'stores/TeamStore';
import { UiStore } from 'stores/UiStore';
import { LineupSelectors, TeamLineupCenter, TeamName, Title } from './BpSetLineupStyles';

interface IProps {
  history: History;
}

const BpSetLineup: React.FC<IProps> = observer((props) => {
  const taggingStore = useInjection<BpTaggingStore>(ServiceTypes.BpTaggingStore);
  const teamStore = useInjection<TeamStore>(ServiceTypes.TeamStore);
  const lineupStore = useInjection<LineupStore>(ServiceTypes.LineupStore);
  const sessionStore = useInjection<SessionStore>(ServiceTypes.SessionStore);
  const contextualDataStore = useInjection<ContextualDataStore>(ServiceTypes.ContextualDataStore);
  const uiStore = useInjection<UiStore>(ServiceTypes.UiStore);
  const contextStore = useInjection<ContextStore>(ServiceTypes.ContextStore);
  const rulesetService = useInjection<RulesetService>(ServiceTypes.RulesetService);
  const { history } = props;
  const [isHomeTeamLoading, setIsHomeTeamLoading] = useState(false);
  const [homeTeamManager, setHomeTeamManager] = useState<LineupManager>();
  const notificationStore = useInjection<NotificationStore>(ServiceTypes.NotificationStore);

  useEffect(() => {
    // Needs to be on every page, so latest sync is set when user changes page
    contextualDataStore.setLatestSync();
    // This check prevents the user from accessing the page any other way than through the correct flow, since isInitialMetaDataConfirmed is set from the GameDialog
    if (!sessionStore.initialMetadata) {
      history.replace('/');
    }

    uiStore.setIsSettingLineup(true);
    // Clean up
    return () => {
      sessionStore.setInitialMetadata(undefined);
      lineupStore.resetSessionMetaDataValues();
      uiStore.setIsSettingLineup(false);
    };
  }, []);

  useEffect(() => {
    teamStore.fetchTeams();
    if (!sessionStore.isSessionStarted && sessionStore.initialMetadata) {
      changeSessionMode();
    }
  }, []);

  const changeSessionMode = async () => {
    sessionStore.setIsOperatorlessTaggingEnabledForSelectedSession(false);
    const isSuccess = await sessionStore.setCurrentSessionMode({
      sessionMode: SessionMode.SessionStarted,
      sessionType: SessionType.BattingPractice,
      systemId: contextStore.activeField!.systemId,
      time: '', // will be set in store anyway
      isVideoCaptureEnabled: lineupStore.isVideoCaptureEnabled,
      isPlayerTrackingEnabled: lineupStore.isPlayerTrackingEnabled,
      issuer: 'TaggingApp',
      ...sessionStore.initialMetadata!,
    });
    if (!isSuccess) {
      notificationStore.addNotification({
        id: 'session-mode-change-error',
        message: `There was an error with session mode change. Please go back to main screen and start over. Remember, that running the app in multiple tabs at the same time is not supported.`,
        variant: NotificationType.error,
        removable: true,
        onClose: () => {
          notificationStore.removeNotification('session-mode-change-error');
        },
      });
    }
  };
  const onHomeTeamSelected = async (team?: ITeam) => {
    if (team) {
      setIsHomeTeamLoading(true);
      const roster = await teamStore.fetchPlayers(team.id);

      const activeStrikeZone =
        lineupStore.overridedStrikeZone?.activeStrikeZone ||
        sessionStore.currentSessionMetadata.strikeZone?.customDefinition?.activeStrikeZone ||
        sessionStore.currentSessionMetadata.strikeZone?.activeStrikeZone;

      setHomeTeamManager(
        new LineupManager(
          team,
          lineupStore.ruleset,
          roster?.players || [],
          activeStrikeZone,
          rulesetService.getPlayerLimit(lineupStore.ruleset),
          [],
          true
        )
      );
      setIsHomeTeamLoading(false);
    } else {
      setHomeTeamManager(undefined);
    }
  };

  const isStartTaggingDisabled = () => {
    return !homeTeamManager?.isValidated || !sessionStore.isGameInProgress;
  };

  const startTagging = async () => {
    if (!homeTeamManager) {
      return;
    }
    await taggingStore.initializeFromLineup(homeTeamManager, lineupStore.owningOrganization.id);
    lineupStore.resetSessionMetaDataValues();
    history.push('/tagging-bp');
  };

  return (
    <div>
      <LineupSelectors>
        <TeamLineupCenter>
          <TeamName>
            {teamStore.isLoading ? (
              <div>
                <Loader />
              </div>
            ) : (
              <>
                <Title>Team</Title>

                <TeamSelector
                  placeholder='Click to search for a team'
                  onSelectionChange={(t) => {
                    onHomeTeamSelected(t);
                  }}
                  selectedTeam={homeTeamManager?.team}
                  isReadOnly={false}
                />
              </>
            )}
          </TeamName>
          <TeamSetup
            lineupManager={homeTeamManager}
            isLoading={isHomeTeamLoading}
            isPlaceholder={!homeTeamManager}
            unavailablePlayerIds={[]}
            size='lg'
            containerClass='.container'
          />
        </TeamLineupCenter>
      </LineupSelectors>
      <SetLineupBottomBar
        sessionMode={sessionStore.currentSessionMode}
        onClickStartTaggingCallback={startTagging}
        startTaggingDisabled={isStartTaggingDisabled()}
        isLoading={taggingStore.isLoading}
        onClickSwapTeamsCallback={() => {}}
        isBattingPractice={true}
      />
    </div>
  );
});

export default BpSetLineup;
